import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { ImpersonationContextKey } from '../../../models';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { dateInputToIsoFormat } from '../../../models/utils/dateUtils';
import { useTemplateCreateMutation } from '../../../queries';
import {
	useTemplateKeywords,
	useTemplateKeywordsForAccount,
} from '../../../queries/Templates/useEmailTemplateKeywords';
import { hasCccKeyword } from '../../containers/Campaigns/utils';
import { charmGray } from '../../styles/colors';
import { bs } from '../../styles/styles';
import { AutoCompleteSearchField } from '../autocomplete/AutoCompleteSearchField';
import { Checkbox } from '../Checkbox';
import { DateInput } from '../../components-v2/DateInput';
import { LoadingSpinner } from '../LoadingSpinner';
import { PillMultiSelect } from '../PillMultiSelect/PillMultiSelect';
import { CalendarIcon } from '../svgs/icons/CalendarIcon';
import { ExpiryIcon } from '../svgs/icons/ExpiryIcon';
import { KeywordIcon } from '../svgs/icons/KeywordIcon';
import { TextInputFormField } from '../TextInputFormField';
import { styleSheet } from './styles';

interface Props {
	canImpersonateOnTemplateSave?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	template: Api.ITemplate;
	onSuccess: (template: Api.ITemplate) => void;
	onCancel: () => void;
	enableKeywords?: boolean;
	industry?: Api.Industry;
}

const _SaveTemplate = (props: Props) => {
	const { logApiError } = useEventLogging('SaveTemplate');
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const [name, setName] = React.useState('');
	const [selectedImpersonatedAuthor, setSelectedImpersonatedAuthor] = React.useState<Api.IUser>(null);
	const [selectedTemplateScope, setSelectedTemplateScope] = React.useState<
		Api.TemplateScope.User | Api.TemplateScope.Account
	>(props.template?.scope === Api.TemplateScope.Account ? Api.TemplateScope.Account : Api.TemplateScope.User);
	const [selectedKeywords, setSelectedKeywords] = React.useState<string[]>([]);
	const [suggestedDate, setSuggestedDate] = React.useState<string>('');
	const [expirationDate, setExpirationDate] = React.useState<string>('');
	const templateCreateMutation = useTemplateCreateMutation({
		onError: error => {
			logApiError('SaveEmailMessageTemplate-SaveError', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: template => {
			props.onSuccess(template);
		},
	});

	// Use account-specific keywords when impersonating, otherwise use industry keywords
	const isImpersonating = !!props.impersonationContext?.account?.id;
	const accountIndustry = userSession.account?.additionalInfo?.industry;

	const industryKeywordsQuery = useTemplateKeywords({
		industry: props.industry || accountIndustry,
		enabled: !!props.enableKeywords && !isImpersonating && !!(props.industry || accountIndustry),
	});

	const accountKeywordsQuery = useTemplateKeywordsForAccount({
		impersonationContext: props.impersonationContext,
		enabled: !!props.enableKeywords && isImpersonating,
	});

	const keywordsQuery = isImpersonating ? accountKeywordsQuery : industryKeywordsQuery;
	const showCccDateFields = props.enableKeywords && hasCccKeyword({ keywords: selectedKeywords });
	const handleImpersonatedAuthorSelected = (user: Api.IUser) => {
		setSelectedImpersonatedAuthor(user);
	};
	const handleTemplateScopeToggle = () => {
		setSelectedTemplateScope(templateScope => {
			return templateScope === Api.TemplateScope.Account ? Api.TemplateScope.User : Api.TemplateScope.Account;
		});
	};

	const handleKeywordSelect = (keyword: string) => {
		if (keyword && !selectedKeywords.includes(keyword)) {
			setSelectedKeywords([...selectedKeywords, keyword]);
		}
	};

	const handleKeywordRemove = (keyword: string) => {
		setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
	};

	const handleSave = () => {
		const trimmedName = name.trim();
		if (!trimmedName) {
			errorMessages.push({
				messages: ['Please enter a name'],
			});
			return;
		}
		const templateToSave = {
			...props.template,
			name: trimmedName,
			scope: selectedTemplateScope,
		};
		delete templateToSave.id;
		if (selectedImpersonatedAuthor) {
			templateToSave.creator = selectedImpersonatedAuthor as Api.IUserReference;
		}

		const systemScopes = [Api.TemplateScope.System, Api.TemplateScope.Industry];
		const isSystemTemplate = systemScopes.includes(props.template?.scope);
		if (isSystemTemplate) {
			delete templateToSave.keywords;
		} else if (props.enableKeywords && selectedKeywords.length > 0) {
			templateToSave.keywords = selectedKeywords;
		}

		// Include CCC date fields in schedule object if set
		if (showCccDateFields && (suggestedDate || expirationDate)) {
			templateToSave.schedule = {
				startDate: suggestedDate ? dateInputToIsoFormat(suggestedDate) : null,
				expirationDate: expirationDate ? dateInputToIsoFormat(expirationDate) : null,
			};
		}

		templateCreateMutation.mutate({
			template: templateToSave,
			impersonationContext: selectedImpersonatedAuthor
				? { ...props.impersonationContext, user: selectedImpersonatedAuthor }
				: props.impersonationContext,
		});
	};

	return (
		<div className={css(bs.boxBorder)}>
			<div className={css(bs.textTitles, bs.textLg)}>Save as New Template</div>
			<div className={css(bs.pt6, bs.relative)}>
				<div>
					<div className={css(bs.textHeader, bs.textXs)}>
						Template Name:
						<span className='required'>*</span>
					</div>
					<TextInputFormField
						className={css(bs.mt1)}
						onChange={ev => setName(ev.target.value)}
						type='text'
						value={name}
					/>
				</div>
				{templateCreateMutation.isPending ? <LoadingSpinner className={css(bs.absoluteCenter)} type='large' /> : null}
				{props.canImpersonateOnTemplateSave && props.impersonationContext ? (
					<div className={css(styleSheet.accountUsersSelectContainer)}>
						<div className={css(bs.textHeader, bs.textXs)}>You are creating this template for...</div>
						<AutoCompleteSearchField
							inputId='employeeSearchBox2'
							inputProps={{ placeholder: 'Search' }}
							onItemSelected={handleImpersonatedAuthorSelected}
							placeholder='Search account users'
							preventImpersonation={false}
							resultsLimit={5}
							type={Api.ResourceAutoCompleteViewModelType.User}
						/>
					</div>
				) : null}
				{props.enableKeywords && !templateCreateMutation.isPending ? (
					<div className={css(bs.mt4)}>
						<div className={css(bs.textHeader, bs.textXs, bs.mb2)}>Keywords:</div>
						<PillMultiSelect
							variant='keyword'
							accessory={<KeywordIcon fillColor={charmGray} />}
							addButtonLabel='Add Keyword'
							options={keywordsQuery.data || []}
							selectedOptions={selectedKeywords}
							onEditOptions={handleKeywordSelect}
							onRemove={handleKeywordRemove}
							onOptionClick={handleKeywordSelect}
							disabled={keywordsQuery.isPending}
						/>
					</div>
				) : null}
				{showCccDateFields ? (
					<div className={css(bs.mt4, bs.flex, bs.gap4)}>
						<div className={css(bs.flex1)}>
							<div className={css(bs.textHeader, bs.textXs, bs.mb2, bs.flex, bs.itemsCenter, bs.gap1)}>
								<CalendarIcon width={14} height={14} />
								Suggested Use Date:
							</div>
							<DateInput value={suggestedDate} onChange={setSuggestedDate} />
						</div>
						<div className={css(bs.flex1)}>
							<div className={css(bs.textHeader, bs.textXs, bs.mb2, bs.flex, bs.itemsCenter, bs.gap1)}>
								<ExpiryIcon width={14} height={14} fill='currentColor' />
								Expiration Date:
							</div>
							<DateInput value={expirationDate} onChange={setExpirationDate} />
						</div>
					</div>
				) : null}
				<div className={css(bs.mt4)}>
					<div className={css(bs.flex, bs.itemsCenter, bs.justifyBetween, bs.mb5, bs.textSm)}>
						<Checkbox
							checked={selectedTemplateScope === Api.TemplateScope.Account}
							onChange={handleTemplateScopeToggle}
						>
							<span>Share template with co-workers</span>
						</Checkbox>
					</div>
					<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
						<button className={css(bs.ctaButton)} disabled={templateCreateMutation.isPending} onClick={handleSave}>
							Save
						</button>
						<button
							className={css(bs.ctaButtonReverse)}
							disabled={templateCreateMutation.isPending}
							onClick={props.onCancel}
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

const SaveTemplateWithObserver = observer(_SaveTemplate);
export const SaveTemplate = inject(ImpersonationContextKey)(SaveTemplateWithObserver);
